import { format, differenceInDays, isPast, isFuture, startOfMonth, endOfMonth, isWithinInterval, addMonths } from 'date-fns';

/**
 * Experience status values
 */
export const EXPERIENCE_STATUS = {
  PLANNING: 'planning',
  UPCOMING: 'upcoming',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
};

/**
 * Get experience status based on date and current phase
 */
export function getExperienceStatus(experience) {
  // If explicitly set to archived, return archived
  if (experience.status === EXPERIENCE_STATUS.ARCHIVED) {
    return EXPERIENCE_STATUS.ARCHIVED;
  }

  // If explicitly set to completed, return completed
  if (experience.status === EXPERIENCE_STATUS.COMPLETED) {
    return EXPERIENCE_STATUS.COMPLETED;
  }

  // If explicitly set to upcoming, return upcoming
  if (experience.status === EXPERIENCE_STATUS.UPCOMING) {
    return EXPERIENCE_STATUS.UPCOMING;
  }

  // Auto-determine status based on date
  if (!experience.date) {
    return EXPERIENCE_STATUS.PLANNING;
  }

  const experienceDate = experience.date?.toDate ? experience.date.toDate() : new Date(experience.date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isPast(experienceDate)) {
    return EXPERIENCE_STATUS.COMPLETED;
  }

  // If date is in the future and experience is in execution phase, it's upcoming
  if (isFuture(experienceDate) && experience.currentPhase === 'execution') {
    return EXPERIENCE_STATUS.UPCOMING;
  }

  // Default to planning
  return EXPERIENCE_STATUS.PLANNING;
}

/**
 * Get days until experience date
 */
export function getDaysUntil(experience) {
  if (!experience.date) return null;
  
  const experienceDate = experience.date?.toDate ? experience.date.toDate() : new Date(experience.date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  experienceDate.setHours(0, 0, 0, 0);
  
  return differenceInDays(experienceDate, today);
}

/**
 * Get status badge text and color
 */
export function getStatusBadge(experience) {
  const status = getExperienceStatus(experience);
  const daysUntil = getDaysUntil(experience);

  switch (status) {
    case EXPERIENCE_STATUS.COMPLETED:
      return { text: 'Completed', color: 'bg-gray-100 text-gray-700' };
    case EXPERIENCE_STATUS.UPCOMING:
      if (daysUntil !== null && daysUntil <= 7) {
        return { text: `In ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`, color: 'bg-red-100 text-red-700' };
      }
      return { text: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    case EXPERIENCE_STATUS.PLANNING:
      return { text: 'Planning', color: 'bg-yellow-100 text-yellow-700' };
    case EXPERIENCE_STATUS.ARCHIVED:
      return { text: 'Archived', color: 'bg-gray-100 text-gray-400' };
    default:
      return { text: 'Planning', color: 'bg-yellow-100 text-yellow-700' };
  }
}

/**
 * Group experiences by time period
 */
export function groupExperiencesByTime(experiences) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const thisMonth = {
    start: startOfMonth(today),
    end: endOfMonth(today),
  };
  
  const nextMonth = {
    start: startOfMonth(addMonths(today, 1)),
    end: endOfMonth(addMonths(today, 1)),
  };

  const groups = {
    thisMonth: [],
    nextMonth: [],
    later: [],
  };

  experiences.forEach(experience => {
    if (!experience.date) {
      groups.later.push(experience);
      return;
    }

    const experienceDate = experience.date?.toDate ? experience.date.toDate() : new Date(experience.date);
    experienceDate.setHours(0, 0, 0, 0);

    if (isWithinInterval(experienceDate, thisMonth)) {
      groups.thisMonth.push(experience);
    } else if (isWithinInterval(experienceDate, nextMonth)) {
      groups.nextMonth.push(experience);
    } else {
      groups.later.push(experience);
    }
  });

  return groups;
}

/**
 * Sort experiences by date (soonest first)
 */
export function sortExperiencesByDate(experiences) {
  return [...experiences].sort((a, b) => {
    // Experiences without dates go to the end
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;

    const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
    const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);

    return dateA - dateB;
  });
}

/**
 * Filter experiences by status
 */
export function filterExperiencesByStatus(experiences, statusFilter) {
  if (statusFilter === 'all') {
    return experiences.filter(e => getExperienceStatus(e) !== EXPERIENCE_STATUS.ARCHIVED);
  }
  
  if (statusFilter === 'active') {
    return experiences.filter(e => {
      const status = getExperienceStatus(e);
      return status === EXPERIENCE_STATUS.PLANNING || status === EXPERIENCE_STATUS.UPCOMING;
    });
  }
  
  if (statusFilter === 'past') {
    return experiences.filter(e => getExperienceStatus(e) === EXPERIENCE_STATUS.COMPLETED);
  }

  return experiences.filter(e => getExperienceStatus(e) === statusFilter);
}

/**
 * Filter experiences by type
 */
export function filterExperiencesByType(experiences, typeFilter) {
  if (typeFilter === 'all') return experiences;
  return experiences.filter(e => e.type === typeFilter);
}

/**
 * Search experiences by name
 */
export function searchExperiences(experiences, searchQuery) {
  if (!searchQuery.trim()) return experiences;
  
  const query = searchQuery.toLowerCase();
  return experiences.filter(e => 
    e.title?.toLowerCase().includes(query) ||
    e.type?.toLowerCase().includes(query)
  );
}

/**
 * Calculate progress percentage based on phase and decisions
 */
export function calculateProgress(experience) {
  if (!experience?.decisions) return 0;
  
  const decisions = Object.values(experience.decisions);
  if (decisions.length === 0) return 0;
  
  const completed = decisions.filter(d => d.status === 'locked').length;
  return Math.round((completed / decisions.length) * 100);
}

/**
 * Format experience date for display
 */
export function formatExperienceDate(experience) {
  if (!experience.date) return 'Date TBD';
  
  const experienceDate = experience.date?.toDate ? experience.date.toDate() : new Date(experience.date);
  return format(experienceDate, 'MMM d, yyyy');
}

// Legacy exports for backward compatibility during migration
export const PROJECT_STATUS = EXPERIENCE_STATUS;
export const getProjectStatus = getExperienceStatus;
export const groupProjectsByTime = groupExperiencesByTime;
export const sortProjectsByDate = sortExperiencesByDate;
export const filterProjectsByStatus = filterExperiencesByStatus;
export const filterProjectsByType = filterExperiencesByType;
export const searchProjects = searchExperiences;
export const formatProjectDate = formatExperienceDate;

